import '../../pages/ExportAthletesPage.css';

import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import PageTopNav from '../../components/common/PageTopNav/PageTopNav';
import { copyTextToClipboard } from '../../utils/clipboard';
import { useDemoStore } from '../store/DemoStoreContext';

const DemoExportAthletesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tryoutMeta, athletes } = useDemoStore();

  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [showNames, setShowNames] = useState(true);
  const [showNumbers, setShowNumbers] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  const filteredText = useMemo<string | null>(() => {
    if (selectedTeam === null) return null;

    const sorted = athletes
      .filter(a => a.teamPlacement === selectedTeam)
      .sort((a, b) => {
        const na = parseInt(a.number, 10);
        const nb = parseInt(b.number, 10);
        if (isNaN(na) && isNaN(nb)) return 0;
        if (isNaN(na)) return 1;
        if (isNaN(nb)) return -1;
        return na - nb;
      });

    if (sorted.length === 0) return '';

    return sorted
      .map(a => {
        if (showNumbers && showNames) return `#${a.number} - ${a.name}`;
        if (showNumbers) return `#${a.number}`;
        if (showNames) return a.name;
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }, [athletes, selectedTeam, showNames, showNumbers]);

  const handleCopy = useCallback((): void => {
    if (!filteredText) return;
    void copyTextToClipboard(filteredText).then(() => {
      setCopySuccess(true);
      if (copyTimeoutRef.current != null) window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopySuccess(false);
        copyTimeoutRef.current = null;
      }, 2000);
    });
  }, [filteredText]);

  return (
    <div className="export-athletes-shell">
      <PageTopNav
        title="Export Athletes"
        onBack={() => navigate('/demo/tryout')}
        backAriaLabel="Back to tryout"
      />

      <div className="export-athletes-content">
        <section className="export-athletes-section">
          <p className="export-athletes-label">Team</p>
          <div className="export-athletes-team-row">
            {tryoutMeta.teams.map(team => (
              <button
                key={team}
                type="button"
                className={[
                  'export-athletes-team-btn',
                  selectedTeam === team ? 'export-athletes-team-btn--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setSelectedTeam(prev => prev === team ? null : team)}
              >
                {team}
              </button>
            ))}
          </div>
        </section>

        <section className="export-athletes-section">
          <p className="export-athletes-label">Display</p>
          <div className="export-athletes-checkbox-row">
            <label className="export-athletes-checkbox-label">
              <input
                type="checkbox"
                checked={showNumbers}
                onChange={e => {
                  if (!e.target.checked && !showNames) setShowNames(true);
                  setShowNumbers(e.target.checked);
                }}
              />
              Show Numbers
            </label>
            <label className="export-athletes-checkbox-label">
              <input
                type="checkbox"
                checked={showNames}
                onChange={e => {
                  if (!e.target.checked && !showNumbers) setShowNumbers(true);
                  setShowNames(e.target.checked);
                }}
              />
              Show Names
            </label>
          </div>
        </section>

        {selectedTeam !== null && filteredText !== null && (
          <section className="export-athletes-section">
            <div className="export-athletes-result-header">
              <p className="export-athletes-label">{selectedTeam}</p>
              <button
                type="button"
                className="export-athletes-copy-btn"
                onClick={handleCopy}
                disabled={filteredText === ''}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {filteredText === '' ? (
              <p className="export-athletes-empty">No athletes placed on this team.</p>
            ) : (
              <pre className="export-athletes-pre">{filteredText}</pre>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default DemoExportAthletesPage;
